/**
 * Conditional-logic and progress helpers for the onboarding questionnaire.
 *
 * Pure functions, extracted from useFillQuestionnaire under CR-6165 to keep
 * that hook under the 500-line limit. Behaviour is unchanged — these are the
 * same evaluations that previously lived inside FillQuestionnaire.tsx.
 */
import { Question, Section, ConditionalLogic, FilledDatasetDetail, AnswerValue } from './questionnaireTypes';

type Answers = Record<number, AnswerValue>;

/** True when an answer counts as "not given". */
export function isAnswerEmpty(answer: AnswerValue | undefined): boolean {
  return answer === undefined || answer === null || answer === '' ||
         (Array.isArray(answer) && answer.length === 0);
}

/**
 * Evaluate a question's or section's show_if rules against current answers.
 *
 * @param logic   The conditional logic block, if any.
 * @param answers Current answer map.
 * @returns true when the element should be visible.
 */
export function evaluateConditionalLogic(logic: ConditionalLogic | undefined, answers: Answers): boolean {
  if (!logic || !logic.show_if || logic.show_if.length === 0) return true;

  const evaluateCondition = (condition: NonNullable<ConditionalLogic['show_if']>[0]): boolean => {
    const { question_id, operator, value } = condition;
    const answer = answers[question_id];

    const answerStr = Array.isArray(answer) ? answer.join(',') : String(answer ?? '');
    const answerIsEmpty = isAnswerEmpty(answer);

    switch (operator) {
      case 'equals':
        if (Array.isArray(answer)) return answer.includes(String(value));
        return String(answer) === String(value) || answer === value;
      case 'not_equals':
        if (Array.isArray(answer)) return !answer.includes(String(value));
        return String(answer) !== String(value) && answer !== value;
      case 'contains':
        return answerStr.toLowerCase().includes(String(value ?? '').toLowerCase());
      case 'not_contains':
        return !answerStr.toLowerCase().includes(String(value ?? '').toLowerCase());
      case 'is_empty':
        return answerIsEmpty;
      case 'is_not_empty':
        return !answerIsEmpty;
      default:
        return true;
    }
  };

  const logicType = logic.logic || 'and';
  if (logicType === 'or') return logic.show_if.some(evaluateCondition);
  return logic.show_if.every(evaluateCondition);
}

/** Find a question anywhere in the dataset by id. */
export function findQuestion(dataset: FilledDatasetDetail | null, questionId: number): Question | undefined {
  if (!dataset?.dataset_detail?.sections) return undefined;
  for (const section of dataset.dataset_detail.sections) {
    const question = section.questions?.find(q => q.id === questionId);
    if (question) return question;
  }
  return undefined;
}

/** Every question currently visible under the active conditional logic. */
export function getVisibleQuestions(dataset: FilledDatasetDetail | null, answers: Answers): Question[] {
  if (!dataset?.dataset_detail?.sections) return [];
  const visible: Question[] = [];
  for (const section of dataset.dataset_detail.sections) {
    if (!evaluateConditionalLogic(section.conditional_logic, answers)) continue;
    for (const question of section.questions || []) {
      if (evaluateConditionalLogic(question.conditional_logic, answers)) visible.push(question);
    }
  }
  return visible;
}

/** Visible, required questions that still have no answer. */
export function getMissingRequiredQuestions(dataset: FilledDatasetDetail | null, answers: Answers): Question[] {
  return getVisibleQuestions(dataset, answers)
    .filter(question => question.is_required && isAnswerEmpty(answers[question.id]));
}

/** How many visible questions have an answer. */
export function getAnsweredCount(dataset: FilledDatasetDetail | null, answers: Answers): number {
  return getVisibleQuestions(dataset, answers)
    .filter(question => !isAnswerEmpty(answers[question.id])).length;
}

export type { Section };
